import BotoesSalvarExcluir from "@/shared/components/forms/BotoesSalvarExcluir";
import Carregando from "@/shared/components/Carregando";
import Links from "@/shared/constants/navegacao";
import ModalExcluir from "@/shared/components/modals/ModalExcluir";
import ModalHistorico from "@/modules/system/historico/ModalHistorico";
import { ColaboradorProfile } from "../forms/profiles/colaborador-profile";
import { CreatePersonDto, ReadPapelDto, ReadPersonDto, UpdatePersonDto } from "@/api.types";
import { DataIso } from "@/shared/utils/datas";
import { DefensorProfile } from "../forms/profiles/defensor-profile";
import { EditadoPorRodape } from "@/shared/components/EditadoPorRodape";
import { EstagiarioProfile } from "../forms/profiles/estagiario-profile";
import { FormProvider, useForm } from "react-hook-form";
import { PermissionEnum } from "@/modules/system/papeis/enum/permission.enum";
import { RoleEnum } from "@/modules/system/papeis/enum/role.enum";
import { TabDadosPessoais } from "../forms/form.dadospessoais";
import { Tabelas } from "@/shared/constants/tables";
import { UsuarioProfile } from "../forms/profiles/usuario-profile";
import { ValidarPermissaoUsuario } from "@/modules/system/auth/application/validate-permissions";
import { format } from "date-fns";
import { readPerson, updatePerson, createPerson, deletePerson } from "../../person.service";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBreadcrumbActions } from "@/shared/contexts/BreadcrumbActionsContext";

type PostSaveAction = 'list' | 'continue' | 'addAnother';
import { usePapeis } from "@/modules/system/papeis/papeis.hooks";
import { validaCPF } from "@/shared/utils/cpfs";

interface Props {
  tipo: 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario'
}

export default function PersonEditPage(props: Props) {
  const { id } = useParams();
  const key = id ?? String(Date.now());
  return <PersonEditPageCore key={key} {...props} />;
}

function PersonEditPageCore({ tipo }: Props) {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id !== undefined ? String(params.id) : null;
  const btnSalvar = id ? 'Salvar' : 'Cadastrar';
  const { setActions } = useBreadcrumbActions();

  const podeVerDefensor = ValidarPermissaoUsuario(RoleEnum.DEFENSOR, PermissionEnum.UPDATE)
  const podeVerEstagiario = ValidarPermissaoUsuario(RoleEnum.ESTAGIARIO, PermissionEnum.UPDATE)
  const podeVerColaborador = ValidarPermissaoUsuario(RoleEnum.COLABORADOR, PermissionEnum.UPDATE)
  const podeVerUsuarios = ValidarPermissaoUsuario(RoleEnum.USUARIO, PermissionEnum.UPDATE)

  const [isExecuting, setIsExecuting] = useState(false);

  const methods = useForm<ReadPersonDto>({ shouldUnregister: false });
  const { getValues } = methods;

  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadedLists, setLoadedLists] = useState(false);
  const [papeis, setPapeis] = useState<ReadPapelDto[]>([]);

  const { indexLink, createLink, editLink } = useMemo(() => {
    switch (tipo) {
      case 'colaborador':
        return { indexLink: `${Links.Colaborador_Index}`, createLink: `${Links.Colaborador_Criar}`, editLink: `${Links.Colaborador_Editar}` };
      case 'defensor':
        return { indexLink: `${Links.Defensor_Index}`, createLink: `${Links.Defensor_Criar}`, editLink: `${Links.Defensor_Editar}` };
      case 'estagiario':
        return { indexLink: `${Links.Estagiario_Index}`, createLink: `${Links.Estagiario_Criar}`, editLink: `${Links.Estagiario_Editar}` };
      case 'usuario':
        return { indexLink: `${Links.Usuario_Index}`, createLink: `${Links.Usuario_Criar}`, editLink: `${Links.Usuario_Editar}` };
      default:
        return { indexLink: `${Links.Pessoa_Index}`, createLink: `${Links.Pessoa_Criar}`, editLink: `${Links.Pessoa_Editar}` };
    }
  }, [tipo]);

  const postSaveActionRef = useRef<PostSaveAction | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { data } = usePapeis();
  useEffect(() => {
    if (data) {
      setPapeis(data[0]);
      setLoadedLists(true)
    }
  }, [data]);

  const carregarPessoa = useCallback(async () => {
    if (!id || !loadedLists) return;
    try {
      const res = await readPerson({
        skip: 0,
        take: 1,
        id,
        comTelefone: true,
        comEndereco: true,
        comUsuario: tipo === 'usuario' || tipo === 'todos',
        comDefensor: tipo === 'defensor' || tipo === 'todos',
        comEstagiario: tipo === 'estagiario' || tipo === 'todos',
        comEstagios: tipo === 'estagiario' || tipo === 'todos',
        comColaborador: tipo === 'colaborador' || tipo === 'todos',
      });
      const pessoa = res[0]?.[0];

      if (pessoa) {

        if (pessoa.usuario) {
          pessoa.usuario.corfundotabela = pessoa.usuario.corfundotabela ?? '#e5e5e5';
          pessoa.usuario.corletratabela = pessoa.usuario.corletratabela ?? '#000000';
        }

        const formatDateInput = (value?: string | Date | null, pattern: string = "yyyy-MM-dd") =>
          value ? format(new Date(value), pattern) : "";

        const pessoaFormatada = {
          ...pessoa,
          cpf: pessoa.cpf ? validaCPF(pessoa.cpf, true) : pessoa.cpf,
          dataNascimento: pessoa.dataNascimento ? format(new Date(pessoa.dataNascimento), 'yyyy-MM-dd') : pessoa.dataNascimento,
          foto: pessoa.foto, // Garante que a foto seja incluída
          colaborador: {
            ...pessoa.colaborador,
            data_cessao: pessoa.colaborador?.data_cessao
              ? format(new Date(pessoa.colaborador.data_cessao), "yyyy-MM-dd")
              : "",
            data_saida: pessoa.colaborador?.data_saida
              ? format(new Date(pessoa.colaborador.data_saida), "yyyy-MM-dd")
              : "",
          },
          estagiario: {
            ...pessoa.estagiario,
            estagios: pessoa.estagiario?.estagios?.map(estagio => ({
              ...estagio,
              data_inicio_estagio: formatDateInput(estagio.data_inicio_estagio),
              data_fim_estagio: formatDateInput(estagio.data_fim_estagio),
              desligamento: formatDateInput(estagio.desligamento),
              data_colacao_grau: formatDateInput(estagio.data_colacao_grau, "yyyy-MM"),
            })) ?? []
          }
        };
        methods.reset(pessoaFormatada);
      }
    } catch (error) {
      toast(String(error));
    } finally {
      setLoaded(true);
    }
  }, [id, loadedLists, methods, tipo]);

  useEffect(() => {
    if (!id) {
      methods.reset({});
      setLoaded(true);
      return;
    }
    if (!loadedLists) return;
    carregarPessoa();
  }, [id, loadedLists, carregarPessoa, methods]);

  const handleError = () => { }

  const handleSubmit = (data: ReadPersonDto) => {
    const action: PostSaveAction = postSaveActionRef.current ?? 'list';
    postSaveActionRef.current = null;

    // Ajusta manualmente alguns campos.
    let input = {
      ...data,
      cpf: data.cpf ? data.cpf.replace(/\D/g, '') : data.cpf
    }
    if (input.telefone?.numero)
      input.telefone.numero = input.telefone.numero.replace(/\D/g, '')

    if (input.celular?.numero)
      input.celular.numero = input.celular.numero.replace(/\D/g, '')

    if (!podeVerUsuarios || (tipo !== 'usuario' && tipo !== 'todos') || (!input.usuario?.id && !input.usuario?.ativo))
      delete input.usuario;

    if (!podeVerEstagiario || (tipo !== 'estagiario' && tipo !== 'todos') || (!input.estagiario?.id && !input.estagiario?.ativo))
      delete input.estagiario

    if (!podeVerDefensor || (tipo !== 'defensor' && tipo !== 'todos') || (!input.defensor?.id && !input.defensor?.ativo))
      delete input.defensor

    if (!podeVerColaborador || (tipo !== 'colaborador' && tipo !== 'todos') || (!input.colaborador?.id && !input.colaborador?.ativo))
      delete input.colaborador;

    if (input.colaborador) {
      if (!input.colaborador.funcao?.id) {

        // @ts-expect-error null
        input.colaborador.funcao = null;
      }
    }

    if (input.estagiario) {
      input.estagiario.estagios = input.estagiario?.estagios?.map((item) => {
        return (
          {
            ...item,
            data_colacao_grau: DataIso(item.data_colacao_grau),
            data_inicio_estagio: DataIso(item.data_inicio_estagio),
            data_fim_estagio: DataIso(item.data_fim_estagio),
            desligamento: DataIso(item.desligamento),
          }
        )
      })
    }

    setIsExecuting(true);
    if (id) {
      updatePerson(input as UpdatePersonDto)
        .then(() => {
          toast.success(`Cadastro de ${input.nome} atualizado com sucesso.`);
          if (action === 'list') navigate(indexLink);
          else if (action === 'addAnother') {
            methods.reset({});
            navigate(createLink);
          }
          else if (action === 'continue') carregarPessoa();
        })
        .catch((res) => {
          toast.error(String(res));
        }).finally(() => {
          setIsExecuting(false);
        });
    } else {
      createPerson(input as CreatePersonDto)
        .then((newId) => {
          toast.success(`Cadastro de ${input.nome} realizado com sucesso.`);
          if (action === 'list') navigate(indexLink);
          else if (action === 'addAnother') {
            methods.reset({});
            // Permanece na página de criar para adicionar outro.
          }
          else if (action === 'continue' && newId) {
            navigate(`${editLink}/${newId}`);
          }
        })
        .catch((res) => {
          toast.error(String(res));
        }).finally(() => {
          setIsExecuting(false);
        });
    }
  }

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (id) {
      setModalExcluirShow(false);
      deletePerson(id)
        .then((res) => {
          toast.success(String(res).concat(' pessoa removida com sucesso.'));
          navigate(`${indexLink}`);
        })
        .catch((res) => {
          toast.error(String(res));
        });
    }
  };

  // Registra os botões no Breadcrumb
  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', onClick: () => { methods.reset({}); navigate(createLink); } },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions, navigate, createLink, methods]);

  if (!loaded)
    return <Carregando />

  return (
    <>
      <div className="row">
        <div className="col-12" title="Preencha somente o necessário.">
          <h4 className="pt-3">{methods.watch('nome')} {methods.watch('sobrenome')}
            <small>
              <span className="text-muted ">{getValues().usuario?.ativo ? ' (usuário)' : ''}</span>
              <span className="text-muted ">{getValues().defensor?.ativo ? ' (defensor)' : ''}</span>
              <span className="text-muted ">{getValues().estagiario?.ativo ? ' (estagiário)' : ''}</span>
              <span className="text-muted ">{getValues().colaborador?.ativo ? ' (colaborador)' : ''}</span>
            </small>
          </h4>
        </div>
      </div>
      <hr className="pb-2" />

      <FormProvider {...methods}>
        <form ref={formRef} onSubmit={methods.handleSubmit(handleSubmit, handleError)}>

          <TabDadosPessoais methods={methods} />

          {(tipo === 'defensor' || tipo === 'todos') && <DefensorProfile methods={methods} podeVerDefensor={podeVerDefensor} />}

          {(tipo === 'estagiario' || tipo === 'todos') && <EstagiarioProfile methods={methods} podeVerEstagiario={podeVerEstagiario} />}

          {(tipo === 'colaborador' || tipo === 'todos') && <ColaboradorProfile methods={methods} podeVerColaborador={podeVerColaborador} />}

          {(tipo === 'usuario' || tipo === 'todos') && <UsuarioProfile methods={methods} papeis={papeis} podeVerUsuarios={podeVerUsuarios} />}

          <EditadoPorRodape dados={{ editadoPor: getValues("editadoPor"), editadoEm: getValues("editadoEm") }} />

          <BotoesSalvarExcluir
            isLoading={isExecuting}
            texto={btnSalvar}
            textoLoading=" Salvando..."
            id={id || undefined}
            onExcluirClick={() => setModalExcluirShow(true)}
            onSalvarEAdicionarOutros={() => {
              postSaveActionRef.current = 'addAnother';
              formRef.current?.requestSubmit();
            }}
            onSalvarEContinuar={() => {
              postSaveActionRef.current = 'continue';
              formRef.current?.requestSubmit();
            }}
          />
        </form>
      </FormProvider>

      {id && <>
        <ModalExcluir show={modaExcluirShow} handleExcluir={handleExcluir} handleHide={() => setModalExcluirShow(false)} />
        <ModalHistorico show={modalHistorico} handleHide={() => setModalHistorico(false)} tabela={Tabelas.pessoa} idRegistro={id} />
      </>
      }
    </>
  );
}